import { DateTime, NaiveDate, Tzname, Utc } from "@tento-chrono";
import type { Option } from "@tento-lona/sheets";
import type { ClientLifecycle } from "../client/handle-errors.ts";
import type { PreferencesStorage } from "./preferences-storage.ts";
import { FlightInfo } from "../timeline/flight-info.ts";
import { FlightMapper } from "../timeline/flight-mapper.ts";
import { StoredPreference } from "./stored-preference.ts";
import { ZoneLoad } from "./zone-load.ts";

const STORAGE_KEY = "lona-pref-transitions";

/**
 * One stored timezone transition: its start, end, timezone and label
 * (`null` when it has none).
 */
export type TransitionEntry = {
  readonly id: string;
  readonly start: string;
  readonly end: string;
  readonly timezone: string;
  readonly label: Option<string>;
};

/**
 * The user's stored timezone transitions (trips), each a range in another
 * timezone.
 */
export class TransitionPreferences {
  readonly #storage: PreferencesStorage;
  /** The owning client's liveness: `add` / `remove` check it first. */
  readonly #lifecycle: ClientLifecycle;

  /** `lifecycle`: the owning client's (a standalone face: `new LonaSdk.Client.Lifecycle()`). */
  constructor(storage: PreferencesStorage, lifecycle: ClientLifecycle) {
    this.#storage = storage;
    this.#lifecycle = lifecycle;
  }

  /** Stored transitions; unusable entries are logged and skipped. */
  peekAll(): TransitionEntry[] {
    const list = StoredPreference.array(
      STORAGE_KEY,
      StoredPreference.json(this.#storage, STORAGE_KEY),
    );
    const entries: TransitionEntry[] = [];
    for (const entry of list ?? []) {
      const record = StoredPreference.record(STORAGE_KEY, entry);
      if (record == null) continue;
      const { id, start, end, timezone, label } = record;
      if (
        typeof id === "string" && typeof start === "string" &&
        typeof end === "string" && typeof timezone === "string"
      ) {
        entries.push({
          id,
          start,
          end,
          timezone,
          label: typeof label === "string" ? label : null,
        });
      } else {
        StoredPreference.unusable(
          STORAGE_KEY,
          "entry is not { id, start, end, timezone }",
          JSON.stringify(entry),
        );
      }
    }
    return entries;
  }

  /** Store a transition: `label` is `null` for one with none. */
  add(entry: {
    readonly start: DateTime<Utc>;
    readonly end: DateTime<Utc>;
    readonly timezone: string;
    readonly label: Option<string>;
  }): void {
    this.#lifecycle.assertLive("preferences.transitions.add");
    const entries = this.peekAll();
    entries.push({
      id: crypto.randomUUID(),
      start: entry.start.toString(),
      end: entry.end.toString(),
      timezone: entry.timezone,
      label: entry.label ?? null,
    });
    this.#save(entries);
  }

  remove(id: string): void {
    this.#lifecycle.assertLive("preferences.transitions.remove");
    const entries = this.peekAll().filter((e) => e.id !== id);
    this.#save(entries);
  }

  async getFlights(homeTimezone: string): Promise<FlightInfo<TransitionEntry>[]> {
    return FlightInfo.fromTransitions(this.peekAll(), homeZone(homeTimezone));
  }

  async mapper(homeTimezone: string): Promise<FlightMapper<TransitionEntry>> {
    const flights = await this.getFlights(homeTimezone);
    const local = await ZoneLoad.orElse(homeZone(homeTimezone), Tzname.UTC, "home-zone");
    return new FlightMapper(NaiveDate.today(), flights, local);
  }

  #save(entries: TransitionEntry[]): void {
    this.#storage.setItem(STORAGE_KEY, JSON.stringify(entries));
  }
}

/** `name` as the home zone; a name the runtime does not know as an IANA zone throws. */
function homeZone(name: string): Tzname {
  const zone = Tzname.parseOpt(name);
  if (zone == null) throw new Error(`TransitionPreferences: "${name}" is not an IANA time zone`);
  return zone;
}
